/**
 * Moving readings of a still picture: the picture and its depth map go through
 * one fragment shader, and each effect is a different way of drawing them over
 * time. Nothing is laid on top; the reading itself is what moves.
 */

export const EFFECTS = [
  "depth",
  "contour",
  "heat",
  "edge",
  "blueprint",
  "outline",
  "normals",
  "relight",
  "parallax",
  "halftone",
  "scan",
  "dither",
] as const;
export type Effect = (typeof EFFECTS)[number];

interface ReadingOptions {
  src: string;
  depth: string;
  /** Read every frame, so the effect can change without a remount. */
  effect: () => Effect;
}

const VERTEX = `#version 300 es
in vec2 at;
out vec2 v;
void main() {
  v = vec2(at.x * 0.5 + 0.5, 0.5 - at.y * 0.5);
  gl_Position = vec4(at, 0.0, 1.0);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
in vec2 v;
out vec4 o;
uniform sampler2D pic;
uniform sampler2D dep;
uniform vec2 fit;    // the part of the picture the box shows, as object-fit: cover would
uniform vec2 res;    // canvas size in its own pixels
uniform float unit;  // canvas pixels per CSS pixel
uniform float t;
uniform int fx;

const vec3 INK = vec3(0.02);
const vec3 PAPER = vec3(0.949, 0.949, 0.933);
const vec3 ACID = vec3(0.843, 1.0, 0.0);
const vec3 LILAC = vec3(0.788, 0.722, 0.929);
const vec3 ORANGE = vec3(1.0, 0.416, 0.122);
const vec3 PINK = vec3(1.0, 0.31, 0.639);
const vec3 BLUE = vec3(0.239, 0.42, 1.0);
const float BAYER[16] = float[16](0., 8., 2., 10., 12., 4., 14., 6., 3., 11., 1., 9., 15., 7., 13., 5.);

vec2 uvOf(vec2 p) { return (p - 0.5) * fit + 0.5; }
vec3 P(vec2 p) { return texture(pic, uvOf(p)).rgb; }
float L(vec2 p) { return dot(P(p), vec3(0.299, 0.587, 0.114)); }
float D(vec2 p) { return texture(dep, uvOf(p)).r; }

float sobel(vec2 p, float r) {
  vec2 e = r * unit / res;
  float a = L(p + vec2(-e.x, -e.y)), b = L(p + vec2(0.0, -e.y)), c = L(p + vec2(e.x, -e.y));
  float d = L(p + vec2(-e.x, 0.0)), f = L(p + vec2(e.x, 0.0));
  float g = L(p + vec2(-e.x, e.y)), h = L(p + vec2(0.0, e.y)), i = L(p + vec2(e.x, e.y));
  return length(vec2((c + 2.0 * f + i) - (a + 2.0 * d + g), (g + 2.0 * h + i) - (a + 2.0 * b + c)));
}

vec3 normal(vec2 p) {
  vec2 e = 3.0 * unit / res;
  float dx = D(p - vec2(e.x, 0.0)) - D(p + vec2(e.x, 0.0));
  float dy = D(p - vec2(0.0, e.y)) - D(p + vec2(0.0, e.y));
  return normalize(vec3(dx, dy, 0.03));
}

void main() {
  float d = D(v);
  vec3 col;
  if (fx == 0) {
    // grey depth, near is light; a plane travels through it from far to near
    float slice = smoothstep(0.05, 0.0, abs(d - fract(t * 0.11)));
    col = vec3(d * 0.9 + 0.04) + slice * 0.4;
  } else if (fx == 1) {
    // level lines that flow from far to near
    float x = d * 24.0 - t * 0.35;
    float line = 1.0 - smoothstep(0.0, fwidth(x) * 1.2, abs(fract(x) - 0.5));
    col = mix(INK, ACID, clamp(line + d * d * 0.3, 0.0, 1.0));
  } else if (fx == 2) {
    // depth as heat, with a wave running through it
    float x = clamp(d + 0.05 * sin(d * 22.0 - t * 2.2), 0.0, 1.0);
    col = clamp(vec3(x * 2.4, x * x * 1.7 - 0.08, pow(x, 5.0) * 1.3), 0.0, 1.0);
  } else if (fx == 3) {
    // edges, lit by a wave that travels through depth
    float s = smoothstep(0.1, 0.6, sobel(v, 1.2));
    col = INK + ACID * s * (0.6 + 0.4 * sin(d * 10.0 - t * 1.8));
  } else if (fx == 4) {
    // edges as a blueprint, over a drifting grid
    float s = smoothstep(0.1, 0.55, sobel(v, 1.2));
    vec2 g = fract((v * res / unit + vec2(t * 6.0, 0.0)) / 36.0);
    float grid = max(step(g.x, 0.03), step(g.y, 0.03));
    col = mix(BLUE, PAPER, clamp(s + grid * 0.16, 0.0, 1.0));
  } else if (fx == 5) {
    // ink outline whose weight breathes
    float s = smoothstep(0.12, 0.4, sobel(v, 1.3 + 0.9 * sin(t * 1.2)));
    col = mix(LILAC, INK, s);
  } else if (fx == 6) {
    // surface normals from the depth map, with a light circling over them
    vec3 n = normal(v);
    vec3 light = normalize(vec3(cos(t * 0.8), sin(t * 0.8), 0.7));
    col = n * 0.5 + 0.5 + pow(max(dot(n, light), 0.0), 10.0) * 0.5;
  } else if (fx == 7) {
    // the scene as grey clay under the same circling light
    vec3 n = normal(v);
    vec3 light = normalize(vec3(cos(t * 0.8), sin(t * 0.8), 0.55));
    col = vec3(0.06 + 0.94 * max(dot(n, light), 0.0)) * (0.35 + 0.65 * d);
  } else if (fx == 8) {
    // the picture pushed apart by its depth, as if the camera swayed
    col = P(v + (d - 0.5) * 0.03 * vec2(sin(t * 0.9), 0.6 * cos(t * 0.7)));
  } else if (fx == 9) {
    // halftone: a slowly turning screen of dots
    float s = 9.0 * unit;
    float a = 0.5 + 0.08 * sin(t * 0.4);
    mat2 turn = mat2(cos(a), -sin(a), sin(a), cos(a));
    vec2 q = turn * (v * res);
    vec2 c = (floor(q / s) + 0.5) * s;
    vec2 pc = (transpose(turn) * c) / res;
    float r = s * 0.6 * sqrt(1.0 - L(pc)) * (0.85 + 0.15 * sin(t * 2.0 + D(pc) * 9.0));
    col = mix(ORANGE, INK, 1.0 - smoothstep(r - unit, r + unit, length(q - c)));
  } else if (fx == 10) {
    // scan: rows that rise where the picture is light, scrolling upwards
    float s = 9.0 * unit;
    float y = v.y * res.y;
    float roll = mod(t * 14.0 * unit, s);
    float on = 0.0;
    for (int i = 0; i < 5; i++) {
      float row = (floor((y + roll) / s) + float(i)) * s - roll;
      float lift = L(vec2(v.x, row / res.y)) * 26.0 * unit;
      on = max(on, 1.0 - smoothstep(0.6 * unit, 1.6 * unit, abs(y - (row - lift))));
    }
    col = mix(INK, PAPER, on);
  } else {
    // one-bit dither, with the threshold swelling through depth
    float s = 3.0 * unit;
    vec2 cell = floor(v * res / s);
    float limit = (BAYER[int(mod(cell.y, 4.0)) * 4 + int(mod(cell.x, 4.0))] + 0.5) / 16.0;
    float l = L((cell + 0.5) * s / res) + 0.14 * sin(t * 0.9 + d * 6.0);
    col = l > limit ? PINK : INK;
  }
  o = vec4(col, 1.0);
}`;

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
}

function load(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = src;
  return image.decode().then(() => image);
}

/** Starts drawing on the canvas; returns the function that stops it and frees what it made. */
export function mountReading(canvas: HTMLCanvasElement, options: ReadingOptions): () => void {
  const gl = canvas.getContext("webgl2", { antialias: false, alpha: false });
  const vs = gl && compile(gl, gl.VERTEX_SHADER, VERTEX);
  const fs = gl && compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
  const program = gl?.createProgram();
  // Without WebGL 2 the canvas stays empty and the picture underneath shows through.
  if (!gl || !vs || !fs || !program) return () => undefined;

  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.useProgram(program);
  const uniform = (name: string) => gl.getUniformLocation(program, name);
  const u = { fit: uniform("fit"), res: uniform("res"), unit: uniform("unit"), t: uniform("t"), fx: uniform("fx") };
  gl.uniform1i(uniform("pic"), 0);
  gl.uniform1i(uniform("dep"), 1);

  const buffer = gl.createBuffer();
  const textures: Array<WebGLTexture | null> = [];
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const at = gl.getAttribLocation(program, "at");
  gl.enableVertexAttribArray(at);
  gl.vertexAttribPointer(at, 2, gl.FLOAT, false, 0, 0);

  const texture = (slot: number, image: HTMLImageElement) => {
    gl.activeTexture(gl.TEXTURE0 + slot);
    const made = gl.createTexture();
    textures.push(made);
    gl.bindTexture(gl.TEXTURE_2D, made);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  };

  let alive = true;
  let ready = false;
  let seen = false;
  let raf = 0;
  let aspect = 1;
  let box = { w: 0, h: 0 };
  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const start = performance.now();

  const size = () => {
    box = { w: canvas.clientWidth, h: canvas.clientHeight };
    if (!box.w || !box.h) return;
    // Sharp enough for a window on the picture, without shading a full retina band.
    const scale = Math.min(Math.min(window.devicePixelRatio, 1.5), 1600 / box.w);
    canvas.width = Math.round(box.w * scale);
    canvas.height = Math.round(box.h * scale);
    gl.viewport(0, 0, canvas.width, canvas.height);
  };

  const draw = () => {
    raf = 0;
    if (!alive || !ready || !seen || document.hidden || !box.w || !box.h) return;
    const shape = box.w / box.h;
    gl.uniform2f(u.fit, Math.min(1, shape / aspect), Math.min(1, aspect / shape));
    gl.uniform2f(u.res, canvas.width, canvas.height);
    gl.uniform1f(u.unit, canvas.width / box.w);
    gl.uniform1f(u.t, still ? 0 : (performance.now() - start) / 1000);
    gl.uniform1i(u.fx, EFFECTS.indexOf(options.effect()));
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    raf = requestAnimationFrame(draw);
  };
  const wake = () => {
    if (!raf) raf = requestAnimationFrame(draw);
  };

  const resize = new ResizeObserver(() => {
    size();
    wake();
  });
  resize.observe(canvas);
  const view = new IntersectionObserver(([entry]) => {
    seen = entry.isIntersecting;
    wake();
  });
  view.observe(canvas);
  document.addEventListener("visibilitychange", wake);

  Promise.all([load(options.src), load(options.depth)])
    .then(([pic, dep]) => {
      if (!alive) return;
      aspect = pic.naturalWidth / pic.naturalHeight;
      texture(0, pic);
      texture(1, dep);
      ready = true;
      size();
      wake();
    })
    .catch(() => undefined);

  return () => {
    alive = false;
    if (raf) cancelAnimationFrame(raf);
    resize.disconnect();
    view.disconnect();
    document.removeEventListener("visibilitychange", wake);
    // The canvas keeps its context (React may mount it again), so hand back what was made in it.
    for (const made of textures) gl.deleteTexture(made);
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
  };
}
